// @flow
import * as React from 'react';
import invariant from 'invariant';
import deepEqual from 'react-fast-compare';
import ReactDOM from 'react-dom';
import { connect } from 'react-redux';
import type { CurrentUser } from 'api/currentUser';
import type { RouterHistory } from 'react-router';
import { isBrowser } from 'utils/browser';
import * as localStorage from 'utils/localStorage';
import ScrollWatcherContainer from 'containers/ScrollWatcherContainer';
import Box from 'components/Box';
import { FilestackImage } from 'components/Image';
import IdealistDaysMap from 'components/IdealistDaysMap';
import styled from 'styled-components';
import googleMaps from 'utils/googleMaps';
import SearchHitForMap from 'components/SearchHitForMap';
import IdealistDaysMapAddPinConfirm from 'components/IdealistDaysMapAddPinConfirm';
import GroupsMapAddPinConfirm from 'components/GroupsMapAddPinConfirm';
import IdealistDaysMapConvertPinToEventConfirm from 'components/IdealistDaysMapConvertPinToEventConfirm';
import type { Pins, Pin, PinInfo } from 'api/idealistDaysMaps';
import {
  fetchPin,
  fetchPins,
  fetchIdealistDaysEvents,
  fetchGroups,
  setPin,
  deletePin
} from 'api/idealistDaysMaps';
import type { IdealistDaysMapBlockData } from 'api/cms';
import queryString from 'query-string';
import type { PublicPage } from 'api/website';
import trackError from 'utils/trackError';
import { showModal } from '../../redux/main/actions/modal';
import { setGMapsError } from '../../redux/main/actions/gMapsError';
import { setMapState } from '../../redux/main/actions/daysMap';

const PinText = styled.div`
  white-space: pre-line;
`;

const shareModalTitle = getText(
  'Thank you! Please share this map with your friends.'
);

const shareModalMessage = getText(
  'Join me in connecting our community on Idealist'
);

// Used for testing.
const testMode = localStorage.getItem('testMode');

const PinImageContainer = styled(Box)`
  img {
    width: 100%;
  }
`;

type Props = {
  blockId: string,
  data: IdealistDaysMapBlockData,
  currentUser: ?CurrentUser,
  currentUserIsStaff: boolean,
  showModal: typeof showModal,
  updateCurrentUser: Function,
  content: PublicPage,
  history: RouterHistory,
  gMapsErrored: boolean,
  setMapState: (?('DAYS' | 'GROUPS')) => void,
  setExposeSiteNav: Function
};
type Marker = { pinId: string, data: { setMap: Function } };
type Markers = Array<Marker>;

type MapResultPoint = {
  lat: number,
  lng: number,
  hits: Array<Object>
};

type State = {
  date: string,
  fullScreen: boolean,
  eventOrGroupCount: ?number,
  userPinCount: ?number,
  initialLoad: boolean,
  loaded: boolean,
  showDates: boolean,
  showInfoBox: boolean,
  pins: Pins,
  idealistDayEventOrGroupPins: Array<MapResultPoint>,
  markers: Markers,
  idealistDayEventOrGroupMarkers: Markers,
  initialized: boolean
};

const MARKER_ICONS = {
  IDEALIST_DAY_EVENT: '/static/images/icons/map-marker-days-event.png',
  OWN_PIN: '/static/images/icons/map-marker-days-self.png',
  GENERIC_PIN: '/static/images/icons/map-marker-days-others.png'
};

const createInfoWindow = (maps: Object, node: React.Element<any>) => {
  const wrapper = document.createElement('div');
  const infoWindow = new maps.InfoWindow({
    content: wrapper
  });
  ReactDOM.render(node, wrapper);
  return infoWindow;
};

const LOCAL_STORAGE_PIN_DATA_KEY = 'addingDaysMapPin';

const rememberPinData = (pinData: PinInfo): void => {
  localStorage.setItem(LOCAL_STORAGE_PIN_DATA_KEY, pinData, true);
};

const getRememberedPinData = (): ?PinInfo =>
  localStorage.getItem(LOCAL_STORAGE_PIN_DATA_KEY);

const clearRememberedPinData = (): void => {
  localStorage.removeItem(LOCAL_STORAGE_PIN_DATA_KEY);
};

const getCurrentUserIsStaff = (currentUser: ?CurrentUser) =>
  currentUser !== null &&
  currentUser !== undefined &&
  currentUser.permissions &&
  currentUser.permissions.includes('staff');

class IdealistDaysMapBlock extends React.Component<Props, State> {
  state = {
    date: this.props.data.mapId,
    fullScreen: this.props.data.defaultFullscreen,
    eventOrGroupCount: null,
    userPinCount: null,
    initialLoad: false,
    loaded: false,
    showDates: false,
    showInfoBox: false,
    pins: [],
    idealistDayEventOrGroupPins: [],
    idealistDayEventOrGroupMarkers: [],
    markers: [],
    initialized: false
  };

  fullScreenControlEl = null;

  counterEl = null;

  mapRef = React.createRef<Element>();

  // TODO(arnav): Fix this hack that's getting around a Flow issue.
  map = {};

  infoBoxInitialized = false;

  // TODO(arnav): Fix this hack that's getting around a Flow issue.
  mapsApi = {};

  potentialMarker = null;

  openInfowindow = null;

  centerInitialized = false;

  shareProps = {
    listing: !this.props.data.socialShareLink
      ? this.props.content
      : {
          url: this.props.data.socialShareLink.data.url
        },
    listingType: this.props.data.socialShareLink ? undefined : 'CMS_PAGE',
    title: shareModalTitle,
    messageOverride: this.props.data.socialShareMessage || shareModalMessage
  };

  componentDidMount() {
    this.props.setMapState(
      this.state.fullScreen ? this.props.data.mapType : null
    );
  }

  componentWillReceiveProps(nextProps: Props) {
    const pinData = getRememberedPinData();
    if (
      this.props.currentUser !== nextProps.currentUser &&
      nextProps.currentUser &&
      nextProps.currentUser.location &&
      this.state.loaded &&
      !this.centerInitialized
    ) {
      const center = new this.mapsApi.LatLng(
        nextProps.currentUser.location.latitude,
        nextProps.currentUser.location.longitude
      );
      this.map.setCenter(center);
      this.map.setZoom(8);
    }
    if (
      pinData &&
      this.props.currentUser !== nextProps.currentUser &&
      nextProps.currentUser &&
      nextProps.currentUser.firstName &&
      nextProps.currentUser.lastName &&
      nextProps.currentUser.email
    ) {
      this.props.showModal('ADD_IDEALIST_DAYS_MAP_PIN', {
        prompt: this.props.data.prompt,
        date: this.state.date,
        addPin: this.addPin,
        rememberPinData,
        clearRememberedPinData,
        blockId: this.props.blockId,
        initialValues: pinData,
        mapType: this.props.data.mapType
      });
      clearRememberedPinData();
    } else if (
      !this.infoBoxInitialized &&
      this.props.currentUser !== nextProps.currentUser &&
      nextProps.currentUser &&
      (!nextProps.currentUser.id ||
        // Don't show infobox if Information Required has to be filled out.
        (nextProps.currentUser.firstName &&
          nextProps.currentUser.lastName &&
          nextProps.currentUser.email))
    ) {
      this.setShowInfoBox(true);
    }
    this.infoBoxInitialized = true;
  }

  componentDidUpdate(prevProps: Props, prevState: State) {
    if (!this.mapsApi.version) {
      return;
    }

    if (this.state.fullScreen !== prevState.fullScreen) {
      this.updateFullScreen();
    }
    if (this.state.date !== prevState.date) {
      this.loadIdealistDayEvents();
      this.loadPins();
    }
    if (!deepEqual(this.state.pins, prevState.pins)) {
      this.updatePinsOnMap(prevState.pins);
    }
    if (
      !deepEqual(
        this.state.idealistDayEventOrGroupPins,
        prevState.idealistDayEventOrGroupPins
      )
    ) {
      this.updateIdealistDayEventsOnMap();
    }
  }

  createMap = () =>
    googleMaps()
      .then(maps => {
        this.mapsApi = maps;
        const mapEl = this.mapRef.current;
        if (!mapEl) return;
        const position = new maps.LatLng(0, 0);
        const map = new maps.Map(mapEl, {
          center: position,
          zoom: 3,
          mapTypeControl: false,
          streetViewControl: false,
          gestureHandling: this.state.fullScreen ? 'greedy' : 'cooperative',
          fullscreenControl: false, // remove Google's UI for fullscreen toggle
          clickableIcons: false
        });
        this.map = map;
        maps.event.addListenerOnce(map, 'idle', () => {
          const mapParent = mapEl.parentElement;
          if (mapParent) {
            // tests wait for this to know that the map is loaded
            mapParent.setAttribute('data-maps-loaded', 'true');
          }
        });
        map.addListener('click', e => this.clickMap(e));
        map.addListener('zoom_changed', () => {
          if (!this.state.initialLoad && this.state.showInfoBox) {
            this.setShowInfoBox(false);
          }
        });
      })
      .catch(error => {
        setGMapsError(error.message);
      });

  updateFullScreen = () => {
    this.map.setOptions({
      gestureHandling: this.state.fullScreen ? 'greedy' : 'cooperative'
    });
  };

  toggleFullScreen = () => {
    this.props.setExposeSiteNav(!this.state.fullScreen);
    this.setState(prevState => {
      this.props.setMapState(
        prevState.fullScreen ? null : this.props.data.mapType
      );
      return {
        showInfoBox: false,
        fullScreen: !prevState.fullScreen
      };
    });
  };

  loadIdealistDayEvents = () => {
    fetchIdealistDaysEvents(this.state.date).then(hits => {
      const points = this.hitsToMapResultPoints(hits);
      this.setIdealistDayEventOrGroupPins(points);
    });
  };

  loadGroups = () => {
    fetchGroups().then(hits => {
      const points = this.hitsToMapResultPoints(hits);
      this.setIdealistDayEventOrGroupPins(points);
    });
  };

  loadPins = () =>
    fetchPins(this.state.date).then((pins: Array<Pin>) =>
      this.setPins(pins, true)
    );

  hitsToMapResultPoints = (hits: Array<Object>): Array<MapResultPoint> => {
    const points = hits.reduce((acc, hit) => {
      const { lat, lng } = hit._geoloc; // eslint-disable-line no-underscore-dangle
      const pointKey = `${lat}|${lng}`;
      if (!acc[pointKey]) {
        acc[pointKey] = { lat, lng, hits: [] };
      }
      acc[pointKey].hits.push(hit);
      return acc;
    }, {});
    return Object.keys(points).map(key => points[key]);
  };

  // Events
  addIdealistDaysMapPinOnMap = (point: MapResultPoint) => {
    const markerIcon = {
      url: MARKER_ICONS.IDEALIST_DAY_EVENT,
      scaledSize: new google.maps.Size(33, 45)
    };
    const markerPosition = new this.mapsApi.LatLng(point.lat, point.lng);
    const marker = new this.mapsApi.Marker({
      map: this.map,
      position: markerPosition,
      icon: markerIcon
    });
    if (testMode) {
      marker.setTitle(point.hits[0].name);
    }
    const infoWindow = createInfoWindow(
      this.mapsApi,
      <>
        {point.hits.map((hit, index) => (
          <Box
            key={hit.objectID}
            mt={index > 0 ? '4px' : '2px'}
            width={[`calc(100vw - 48px)`, 400, 480]}
            px="4px"
          >
            <SearchHitForMap
              currentUser={this.props.currentUser}
              hit={hit}
              history={null}
            />
          </Box>
        ))}
      </>
    );
    marker.addListener('click', () => {
      if (this.state.showInfoBox) {
        this.setShowInfoBox(false);
      }
      infoWindow.open(this.map, marker);
      this.openInfowindow = infoWindow;
    });
    this.mapsApi.event.addListener(infoWindow, 'closeclick', () => {
      this.openInfowindow = null;
    });

    return { pinId: point.hits[0].objectId, data: marker };
  };

  updateIdealistDayEventsOnMap = () => {
    const {
      idealistDayEventOrGroupMarkers,
      idealistDayEventOrGroupPins
    } = this.state;
    idealistDayEventOrGroupMarkers.forEach(idealistDayMarker =>
      idealistDayMarker.data.setMap(null)
    );
    const newIdealistDayEventMarkers = idealistDayEventOrGroupPins.map(
      this.addIdealistDaysMapPinOnMap
    );
    this.setState({
      eventOrGroupCount: idealistDayEventOrGroupPins.reduce(
        (acc, cur) => acc + cur.hits.length,
        0
      ),
      idealistDayEventOrGroupMarkers: newIdealistDayEventMarkers
    });
  };

  updatePinsOnMap = (prevPins: Pins) => {
    const { markers, pins } = this.state;

    // If a pin in the current state is not equal to any member of the previous
    // state, that means it a new pin.
    const newPins = pins.filter(pin => {
      if (prevPins.length === 0) return true;
      return !prevPins.some(prevPin => prevPin.id === pin.id);
    });

    // If a pin does exist but has different data, that means it's been
    // updated
    const modifiedPins = pins.filter(pin => {
      const { id: pinId, ...pinData } = pin;
      if (prevPins.length === 0) return true;
      return prevPins.some(prevPin => {
        const { id: prevPinId, ...prevPinData } = prevPin;
        return prevPinId === pinId && !deepEqual(prevPinData, pinData);
      });
    });

    // If a pin in the previous state is not equal to any member of the current
    // state, that means it an old pin. It is also considered an old pin if
    // the two values have the same id but are not equal, since we'll replace it
    // with the updated new pin with the same id.
    const oldPins = prevPins.filter(prevPin => {
      const { id: prevPinId, ...prevPinData } = prevPin;
      return !pins.some(pin => {
        const { id: pinId, ...pinData } = pin;
        return pinId === prevPinId && deepEqual(prevPinData, pinData);
      });
    });

    // If a pin is newly created it should be animated and prompt
    // the Idealist Day Events modal, otherwise it should not.
    // But they are all updated and should be added to the state.
    const updatedPins = newPins
      .map(newPin => ({
        data: newPin,
        animated: !this.state.initialLoad,
        justAdded: !this.state.initialLoad
      }))
      .concat(
        modifiedPins.map(modifiedPin => ({
          data: modifiedPin,
          animated: false,
          justAdded: false
        }))
      );

    const filteredMarkers = markers.filter(marker =>
      oldPins.forEach(oldPin => {
        if (oldPin.id === marker.pinId) {
          marker.data.setMap(null);
          return false;
        }
        return true;
      })
    );

    const newMarkers = updatedPins.map(updatedPin =>
      this.addPinOnMap(
        updatedPin.data,
        updatedPin.animated,
        updatedPin.justAdded
      )
    );

    this.setState({
      initialLoad: false,
      userPinCount: pins.length,
      markers: filteredMarkers.concat(newMarkers)
    });

    const pinId = queryString.parse(window.location.search).pinId;
    const pin = pinId ? pins.find(p => p.id === pinId) : null;
    if (pin) {
      this.props.showModal('VIEW_IDEALIST_DAYS_MAP_PIN', {
        pin,
        date: this.state.date,
        isCurrentUserPin: false,
        currentUserIsStaff: this.props.currentUserIsStaff,
        updatePin: this.updatePin,
        deletePin: this.deletePin,
        convertPin: this.createEvent,
        mapType: this.props.data.mapType
      });
      const center = new this.mapsApi.LatLng(pin.latitude, pin.longitude);
      this.map.setCenter(center);
      this.zoomIn(pin.latitude, pin.longitude, 20);
    }
  };

  addPinOnMap = (
    pin: Pin,
    animated: boolean = false,
    justAdded: boolean = false
  ) => {
    const isCurrentUserPin =
      this.props.currentUser && this.props.currentUser.id === pin.userId;
    const markerPosition = new this.mapsApi.LatLng(pin.latitude, pin.longitude);
    const marker = new this.mapsApi.Marker({
      map: this.map,
      animation: animated ? this.mapsApi.Animation.DROP : null,
      position: markerPosition,
      icon: {
        url: isCurrentUserPin ? MARKER_ICONS.OWN_PIN : MARKER_ICONS.GENERIC_PIN,
        scaledSize: new google.maps.Size(33, 45)
      }
    });
    if (testMode) {
      marker.setTitle(isCurrentUserPin ? 'selfmarker' : pin.text);
    }
    const infoWindow = createInfoWindow(
      this.mapsApi,
      <PinText>{pin.text}</PinText>
    );
    marker.addListener('mouseover', () => {
      if (pin.withProfile) {
        fetchPin(pin.id).then(pinData => {
          if (pinData.profile && pinData.profile.image) {
            const wrapper = document.createElement('div');
            infoWindow.setContent(wrapper);
            const node = (
              <>
                <PinText>
                  <Box display="flex" alignItems="flex-start">
                    {pinData.profile && pinData.profile.image && (
                      <PinImageContainer
                        height={100}
                        width={100}
                        mr={16}
                        flex="0 0 auto"
                      >
                        <FilestackImage
                          round
                          data={pinData.profile.image}
                          height={100}
                          width={100}
                          alt={`Image of ${pinData.profile.firstName ||
                            ''} ${pinData.profile.lastName || ''}`}
                          flex="0 0 auto"
                        />
                      </PinImageContainer>
                    )}
                    <Box>{pin.text}</Box>
                  </Box>
                </PinText>
              </>
            );
            ReactDOM.render(node, wrapper);
          }
        });
      }
      infoWindow.open(this.map, marker);
    });
    marker.addListener('mouseout', () => {
      infoWindow.close();
    });
    marker.addListener('click', () => {
      if (this.state.showInfoBox) {
        this.setShowInfoBox(false);
      }
      this.props.showModal('VIEW_IDEALIST_DAYS_MAP_PIN', {
        pin,
        date: this.state.date,
        isCurrentUserPin,
        currentUserIsStaff: this.props.currentUserIsStaff,
        updatePin: this.updatePin,
        deletePin: this.deletePin,
        convertPin: this.createEvent,
        mapType: this.props.data.mapType
      });
    });
    if (justAdded && this.props.data.mapType === 'DAYS') {
      const convertToEventinfoWindow = createInfoWindow(
        this.mapsApi,
        <IdealistDaysMapConvertPinToEventConfirm
          mapType={this.props.data.mapType}
          confirmConvertPin={() => this.createEvent(pin)}
          cancelConvertPin={() => {
            convertToEventinfoWindow.close();
            this.props.showModal('SHARE_LISTING', this.shareProps);
          }}
        />
      );
      setTimeout(() => {
        convertToEventinfoWindow.open(this.map, marker);
      }, 500);
    }
    return { pinId: pin.id, data: marker };
  };

  createEvent = (pin: Pin) => {
    this.props.history.push(`/post-idealist-day`, {
      description: pin.text.split('\n').join('<br/>'),
      date: this.state.date,
      pinId: pin.id
    });
  };

  createGroup = () => {
    this.props.history.push('/groups/create');
  };

  goToAddress = (coordinates: Object) => {
    if (this.state.showInfoBox) {
      this.setShowInfoBox(false);
    }
    this.zoomIn(coordinates.latitude, coordinates.longitude, 13);
  };

  updatePinOnMap = (pin: Pin) => {
    this.setPins(this.state.pins.map(p => (p.id === pin.id ? pin : p)));
  };

  removePinFromMap = (pin: Pin) => {
    const remainingPins: Array<Pin> = this.state.pins.filter(
      (p: Pin) => p.id !== pin.id
    );
    this.setPins(remainingPins);
  };

  setDate = (date: ?string) => {
    if (date) {
      this.setState({ date });
    }
    this.setState({ showDates: false });
  };

  setPins = (pins: Pins, initialLoad?: boolean) =>
    this.setState({ pins, initialLoad: initialLoad || false });

  setIdealistDayEventOrGroupPins = (pins: Array<MapResultPoint>) =>
    this.setState({ idealistDayEventOrGroupPins: pins });

  setMarkers = (markers: Markers) => this.setState({ markers });

  setIdealistDayEventMarkers = (markers: Markers) => this.setState({ markers });

  setShowDates = (showDates: boolean) => this.setState({ showDates });

  setShowInfoBox = (showInfoBox: boolean) => {
    this.setState({
      showInfoBox
    });
  };

  clickMap = (e: Object) => {
    if (this.state.showInfoBox) {
      this.setShowInfoBox(false);
    }
    this.centerInitialized = true;
    const currentUserId =
      this.props.currentUser && this.props.currentUser.id
        ? this.props.currentUser.id
        : null;
    const userHasPinOnMap =
      this.props.currentUser &&
      this.props.currentUser.id &&
      this.state.pins.filter(pin => pin.userId === currentUserId).length > 0;
    if (userHasPinOnMap) {
      this.props.showModal('IDEALIST_DAYS_MAP_ALERT', {
        message: getText(
          'You already have a pin on this map. If you’d like to change it or delete it, please click on it.'
        )
      });
      return;
    }
    if (this.openInfowindow) {
      this.openInfowindow.close();
    }

    const lat = e.latLng.lat();
    const lng = e.latLng.lng();

    if (this.map.zoom < 8) {
      this.props.showModal('IDEALIST_DAYS_MAP_ZOOM', {
        zoomIn: () => this.zoomIn(lat, lng, 8)
      });
      return;
    }

    if (this.map.zoom < 13) {
      this.props.showModal('IDEALIST_DAYS_MAP_ZOOM', {
        zoomIn: () => this.zoomIn(lat, lng, 13)
      });
      return;
    }

    this.addPotentialMarkerOnMap(lat, lng);
  };

  addPotentialMarkerOnMap = (latitude: number, longitude: number) => {
    const markerPosition = new this.mapsApi.LatLng(latitude, longitude);
    const markerIcon = {
      url: MARKER_ICONS.OWN_PIN,
      scaledSize: new google.maps.Size(33, 45)
    };
    const potentialMarker = new this.mapsApi.Marker({
      map: this.map,
      position: markerPosition,
      icon: markerIcon
    });
    const removePotentialMarker = () => {
      if (potentialMarker) {
        potentialMarker.setMap(null);
      }
    };
    const confirmAddPin = () => {
      this.props.showModal('ADD_IDEALIST_DAYS_MAP_PIN', {
        prompt: this.props.data.prompt,
        date: this.state.date,
        addPin: this.addPin,
        rememberPinData,
        clearRememberedPinData,
        blockId: this.props.blockId,
        initialValues: {
          text: '',
          latitude,
          longitude
        },
        mapType: this.props.data.mapType
      });
      removePotentialMarker();
    };
    let infoWindow;
    if (this.props.data.mapType === 'DAYS') {
      infoWindow = createInfoWindow(
        this.mapsApi,
        <IdealistDaysMapAddPinConfirm
          confirmAddPin={confirmAddPin}
          cancelAddPin={removePotentialMarker}
        />
      );
    } else if (this.props.data.mapType === 'GROUPS') {
      infoWindow = createInfoWindow(
        this.mapsApi,
        <GroupsMapAddPinConfirm
          confirmAddPin={confirmAddPin}
          confirmCreateGroup={this.createGroup}
          cancelAddPin={removePotentialMarker}
        />
      );
    }
    invariant(infoWindow, 'Infowindow not handled for this map type.');
    this.mapsApi.event.addListener(
      infoWindow,
      'closeclick',
      removePotentialMarker
    );
    infoWindow.open(this.map, potentialMarker);
    const mapClickListener = this.map.addListener('click', () => {
      this.mapsApi.event.removeListener(mapClickListener);
      removePotentialMarker();
    });
  };

  zoomIn = (lat: number, lng: number, zoom: number) => {
    const position = new this.mapsApi.LatLng(lat, lng);
    this.map.panTo(position);
    this.map.setZoom(zoom);
  };

  addPin = (pinData: PinInfo): Promise<void> =>
    setPin(this.state.date, pinData).then(pin => {
      this.setPins(this.state.pins.concat([pin]));
    });

  updatePin = (pinData: PinInfo): Promise<Pin> =>
    setPin(this.state.date, pinData).then(pin => {
      this.updatePinOnMap(pin);
      return pin;
    });

  deletePin = (pin: Pin): Promise<void> =>
    deletePin(pin.id).then(() => this.removePinFromMap(pin));

  render() {
    const initializeMap = () => {
      this.setState({ initialized: true });
      this.createMap().then(() => {
        const pinData = getRememberedPinData();
        // Only triggered in the Google sign *up* case.
        if (
          pinData &&
          this.props.currentUser &&
          this.props.currentUser.firstName &&
          this.props.currentUser.lastName &&
          this.props.currentUser.email
        ) {
          this.props.showModal('ADD_IDEALIST_DAYS_MAP_PIN', {
            prompt: this.props.data.prompt,
            date: this.state.date,
            addPin: this.addPin,
            rememberPinData,
            clearRememberedPinData,
            blockId: this.props.blockId,
            initialValues: pinData,
            mapType: this.props.data.mapType
          });
          clearRememberedPinData();
        }

        const mapType = this.props.data.mapType;
        if (mapType === 'DAYS') {
          this.loadIdealistDayEvents();
        } else if (mapType === 'GROUPS') {
          this.loadGroups();
        } else {
          trackError(`The map type is not handled on load: ${mapType}`);
        }
        this.loadPins();
        this.setState({ loaded: true });
        if (testMode) {
          window.mapLoaded = true;
        }
      });
    };
    return (
      <ScrollWatcherContainer offset={[200, 200, 200, 200]}>
        {({ isInViewport }) => {
          if (
            (this.state.fullScreen || isInViewport) &&
            !this.state.initialized &&
            isBrowser()
          ) {
            initializeMap();
          }
          return (
            <IdealistDaysMap
              mapType={this.props.data.mapType}
              instructions={this.props.data.instructions}
              buttonText={this.props.data.buttonText}
              date={this.state.date}
              eventOrGroupCount={this.state.eventOrGroupCount}
              fullScreen={this.state.fullScreen}
              loaded={this.state.loaded}
              selectAddress={this.goToAddress}
              setDate={this.setDate}
              setShowDates={this.setShowDates}
              setShowInfoBox={this.setShowInfoBox}
              shareProps={this.shareProps}
              showDates={this.state.showDates}
              showInfoBox={this.state.showInfoBox}
              toggleFullScreen={this.toggleFullScreen}
              userPinCount={this.state.userPinCount}
              ref={this.mapRef}
              gMapsErrored={this.props.gMapsErrored}
            />
          );
        }}
      </ScrollWatcherContainer>
    );
  }
}

const mapStateToProps = ({ currentUser }) => ({
  currentUserIsStaff: getCurrentUserIsStaff(currentUser)
});

// $FlowFixMe
export default connect(
  mapStateToProps,
  { setMapState }
)(IdealistDaysMapBlock);
